import { useCallback, useEffect, useState } from 'react';
import { plataforma } from '@/platform';
import { authApi, type EstadoAuth } from '../api/authApi';

export type UseAuth = {
  estado: EstadoAuth | null;
  carregando: boolean;
  /** true enquanto o botão "atualizar conexão" da topbar está revalidando. */
  revalidando: boolean;
  erro: string | null;
  recarregar: () => Promise<void>;
  /** Força uma revalidação imediata contra o servidor (botão da topbar). */
  revalidarAgora: () => Promise<void>;
};

// Polling do indicador de conexão. Curto o suficiente para o ponto verde
// virar amarelo logo após o usuário desligar a rede; longo o suficiente
// para não martelar o servidor.
const INTERVALO_REVALIDAR_MS = 30_000;

/**
 * No app web o servidor responde sempre `online` — o que é verdade
 * enquanto a resposta chega. Quando não chega, não existe modo offline
 * para cair: o app não tem banco local. Marcamos `sem-servidor` para a UI
 * dizer isso com honestidade em vez de manter um "Conectado" vencido.
 *
 * No desktop passa reto: lá o próprio backend calcula o status a partir
 * da janela de tolerância offline.
 */
function ajustarConexao(e: EstadoAuth): EstadoAuth {
  if (plataforma.capacidades.offline) return e;
  return plataforma.servidorAlcancavel() ? e : { ...e, status: 'sem-servidor' };
}

/** Mesmo ajuste para o caminho de falha, preservando o resto do estado. */
function marcarSemServidor(anterior: EstadoAuth | null): EstadoAuth | null {
  if (!anterior || plataforma.capacidades.offline) return anterior;
  if (anterior.status === 'sem-servidor') return anterior;
  return { ...anterior, status: 'sem-servidor' };
}

export function useAuth(): UseAuth {
  const [estado, setEstado] = useState<EstadoAuth | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [revalidando, setRevalidando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const recarregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const e = await authApi.estado();
      setEstado(ajustarConexao(e));
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'falha ao obter estado de autenticação');
      setEstado(marcarSemServidor);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void recarregar();
  }, [recarregar]);

  // Assina a mudança de status emitida pela plataforma toda vez que uma
  // tentativa de contato com o servidor (push, /me, /health, numerar)
  // atualiza ultimaTentativaOk. Isso faz o indicador 🟢/🟡 refletir
  // mudanças na hora, sem esperar o polling de 30s — útil principalmente
  // após salvar algo: a sincronização imediata dispara o push, que
  // dispara o evento, que recarrega o estado aqui.
  useEffect(() => {
    const unsubscribe = plataforma.aoMudarStatus(() => {
      void authApi
        .estado()
        .then((e) => setEstado(ajustarConexao(e)))
        .catch(() => setEstado(marcarSemServidor));
    });
    return unsubscribe;
  }, []);

  // Polling silencioso: tenta verificar o servidor a cada 30s sempre que
  // houver alguma conta cacheada — inclusive na tela de login (offline)
  // pra detectar quando o servidor volta. Quando logado usa /revalidar
  // (envia token, move janela de tolerância). Quando deslogado usa /ping
  // (GET /health sem auth, só pra atualizar o indicador de status).
  useEffect(() => {
    if (!estado?.temConta) return;
    const id = setInterval(async () => {
      try {
        if (estado.logado) {
          const novo = await authApi.revalidar();
          setEstado(ajustarConexao(novo));
        } else {
          const r = await authApi.ping();
          setEstado(ajustarConexao(r.estado));
        }
      } catch {
        // Falha esperada quando offline — refresca só com o estado local.
        try {
          const e = await authApi.estado();
          setEstado(ajustarConexao(e));
        } catch {
          // No desktop mantém o estado anterior (o app segue no banco
          // local); no web registra que o servidor está fora.
          setEstado(marcarSemServidor);
        }
      }
    }, INTERVALO_REVALIDAR_MS);
    return () => clearInterval(id);
  }, [estado?.temConta, estado?.logado]);

  const revalidarAgora = useCallback(async () => {
    setRevalidando(true);
    try {
      if (estado?.logado) {
        // Quando logado, dispara sincronização completa: checa servidor,
        // empurra eventos pendentes, pede numeração canônica pra
        // orçamentos provisórios E puxa eventos novos do servidor.
        // Tudo o que o usuário tem "aguardando" tenta sair agora, e o
        // que outras máquinas mandaram entra agora — sem depender do
        // WebSocket estar conectado.
        const r = await authApi.sincronizarAgora();
        setEstado(ajustarConexao(r.estado));
      } else {
        // Sem token (deslogado ou sessão offline encerrada) só dá pra
        // checar conectividade — push exige auth no servidor.
        const r = await authApi.ping();
        setEstado(ajustarConexao(r.estado));
      }
    } catch {
      // Servidor inacessível — atualiza só com o estado local (timestamp
      // não avançou, indicador deve descer pra amarelo).
      try {
        const e = await authApi.estado();
        setEstado(ajustarConexao(e));
      } catch {
        setEstado(marcarSemServidor);
      }
    } finally {
      setRevalidando(false);
    }
  }, [estado?.logado]);

  return { estado, carregando, revalidando, erro, recarregar, revalidarAgora };
}
